import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

interface Identity {
  id: string;
  type: 'user' | 'client';
  client: string;
  exp: number;
  roles: string[];
  permissions: string[];
  scope: string[];
}

interface UserInfo {
  firstName: string;
  lastName: string;
  email: string;
  fullName: string;
  departmentDesc?: string;
  subDepartmentDesc?: string;
  role?: 'super_admin' | 'admin' | null;
}

interface AuthContextType {
  identity: Identity | null;
  user: UserInfo | null;
  loading: boolean;
  error: string | null;
  login: () => void;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  hasRole: (role: string) => boolean;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://finops-portal.angel-admin.angelstudios.com';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkAuth();
    
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get('error');
    if (errorParam) {
      setError(errorParam);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const checkAuth = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/auth/me`, {
        withCredentials: true
      });
      setIdentity(response.data.identity);
      
      // Enrich user with department info and role from backend
      let enrichedUser = response.data.user || null;
      
      // Fetch user role from backend
      if (enrichedUser?.email) {
        try {
          const roleResponse = await axios.get(`${API_BASE_URL}/api/admin/user-role`, {
            withCredentials: true
          });
          if (roleResponse.data.success && roleResponse.data.role) {
            enrichedUser = {
              ...enrichedUser,
              role: roleResponse.data.role
            };
            console.log('[AuthContext] User role:', roleResponse.data.role);
          }
        } catch (error) {
          console.log('[AuthContext] No admin role for user (this is normal for non-admin users)');
        }
        
        // Enrich with department info from employee data
        try {
          console.log('[AuthContext] Loading employee data for user:', enrichedUser.email);
          const { loadEmployees } = await import('../utils/employeeData');
          const employees = await loadEmployees();
          console.log('[AuthContext] Loaded employees:', employees.length);
          const employee = employees.find(emp => 
            emp.workEmail.toLowerCase() === enrichedUser.email.toLowerCase()
          );
          if (employee) {
            console.log('[AuthContext] Found employee match:', employee.employeeName, 'Department:', employee.departmentDesc, 'Sub-Department:', employee.subDepartmentDesc);
            enrichedUser = {
              ...enrichedUser,
              departmentDesc: employee.departmentDesc,
              subDepartmentDesc: employee.subDepartmentDesc
            };
          } else {
            console.warn('[AuthContext] No employee match found for:', enrichedUser.email);
          }
        } catch (error) {
          console.error('[AuthContext] Error loading employee data for user:', error);
          if (error instanceof Error) {
            console.error('[AuthContext] Error details:', error.message);
          }
        }
      }
      
      console.log('[AuthContext] Final user object:', enrichedUser);
      
      setUser(enrichedUser);
      setError(null);
    } catch (err) {
      setIdentity(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = () => {
    window.location.href = `${API_BASE_URL}/api/auth/login`;
  };

  const logout = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
      setIdentity(null);
      setUser(null);
      if (response.data.logoutUrl) {
        window.location.href = response.data.logoutUrl;
      }
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const hasRole = (role: string): boolean => {
    return identity?.roles?.includes(role) ?? false;
  };

  const hasPermission = (permission: string): boolean => {
    return identity?.permissions?.includes(permission) ?? false;
  };

  const value: AuthContextType = {
    identity,
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!identity,
    isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
    isSuperAdmin: user?.role === 'super_admin',
    hasRole,
    hasPermission
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
